import { useMemo, useState, type FormEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import NavBar from '../components/layout/NavBar'
import heroBackdrop from '../assets/pixe.png'
import { register, extractAuthError } from '../api/auth'

const emailOk = (value: string) => /\S+@\S+\.\S+/.test(value.trim())
const pwScore = (pw: string) => {
  let score = 0
  if (pw.length >= 8) score++
  if (/[A-Z]/.test(pw)) score++
  if (/[a-z]/.test(pw)) score++
  if (/\d/.test(pw)) score++
  if (/[^A-Za-z0-9]/.test(pw)) score++
  return Math.min(score, 4)
}

const normalizePhone = (input: string) => {
  const digits = input.replace(/\D+/g, '')
  if (!digits) return ''
  if (digits.length === 10) return `+1${digits}`
  if (digits.length >= 11 && digits.length <= 15) {
    return digits.startsWith('+') ? digits : `+${digits}`
  }
  return ''
}

const featureCards = [
  { label: 'On-chain handles', copy: 'Minted once, portable everywhere.' },
  { label: 'Unified feed', copy: 'Publish longform, drops, and updates in one place.' },
  { label: 'Commerce ready', copy: 'Sell memberships, merch, and digital goods with PIXE.' },
  { label: 'Messaging built-in', copy: 'DM fans, customers, and collaborators with end-to-end privacy.' },
]

const sellingPoints = [
  { title: 'Minutes to mint', detail: 'Reserve a Loop, verify your inbox, and start posting right away.' },
  { title: 'Portable identity', detail: 'Your handle, wallet, and store stay synced across mobile and web.' },
  { title: 'Audience safety', detail: 'Modern auth, optional SMS, and hardware-friendly security defaults.' },
]

export default function Signup() {
  const navigate = useNavigate()
  const referralCode = useMemo(() => {
    try {
      const params = new URLSearchParams(window.location.search)
      const fromQuery = String(params.get('ref') || '').trim()
      if (fromQuery) return fromQuery
      const fromSession = String(sessionStorage.getItem('pixeloop_referral_code') || '').trim()
      if (fromSession) return fromSession
      return String(localStorage.getItem('pixeloop_referral_code') || '').trim()
    } catch {
      return ''
    }
  }, [])
  const [fullName, setFullName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [password, setPassword] = useState('')
  const [showPw, setShowPw] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [info, setInfo] = useState<string | null>(null)

  const trimmedName = fullName.trim()
  const trimmedEmail = email.trim()
  const pwStrength = pwScore(password)
  const ready = trimmedName.length >= 2 && emailOk(trimmedEmail) && pwStrength >= 2 && password.length >= 8

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    if (!ready || submitting) return
    setSubmitting(true)
    setError(null)
    setInfo(null)
    try {
      const normalizedPhone = phone.trim() ? normalizePhone(phone.trim()) : ''
      const payload = {
        full_name: trimmedName,
        email: trimmedEmail,
        password,
        phone: normalizedPhone || undefined,
        referral_code: referralCode || undefined,
      }
      const ticket = await register(payload)
      if (typeof window !== 'undefined') {
        window.sessionStorage.setItem('pixeloop_verification_id', ticket.verification_id)
        window.sessionStorage.setItem('pixeloop_verification_email', trimmedEmail)
        window.sessionStorage.setItem(
          'pixeloop_verification_cooldown',
          String(ticket.resend_cooldown_seconds ?? 120)
        )
      }
      const params = new URLSearchParams({
        email: trimmedEmail,
        verificationId: ticket.verification_id,
        cooldown: String(ticket.resend_cooldown_seconds ?? 120),
      })
      if (ticket.dev_code) params.set('devCode', ticket.dev_code)
      setInfo(`Verification code sent to ${trimmedEmail}.`)
      navigate(`/onboarding/verify?${params.toString()}`, { replace: true })
    } catch (err) {
      setError(extractAuthError(err))
    } finally {
      setSubmitting(false)
    }
  }

  const strengthLabel = useMemo(() => {
    if (pwStrength <= 1) return 'Weak'
    if (pwStrength === 2) return 'Okay'
    if (pwStrength === 3) return 'Strong'
    return 'Great'
  }, [pwStrength])

  return (
    <div className="relative min-h-screen bg-gradient-to-b from-[#050506] via-[#09080d] to-black text-white">
      <div className="pointer-events-none absolute inset-0 opacity-40">
        <img src={heroBackdrop} alt="" className="h-full w-full object-cover blur-[120px]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(255,0,122,0.3),_transparent_60%)]" />
      </div>

      <NavBar showSearch={false} />

      <main className="relative mx-auto w-full max-w-6xl px-4 pb-20 pt-10 sm:px-6">
        <div className="grid gap-12 lg:grid-cols-[1.1fr_minmax(0,0.9fr)] lg:items-start">
          <section className="space-y-8">
            <div className="space-y-5">
              <p className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-1 text-xs font-semibold uppercase tracking-[0.35em] text-white/70">
                Onboarding
              </p>
              <h1 className="text-4xl font-black leading-tight md:text-5xl">
                Claim your handle and join the creator, reader, and shopper network.
              </h1>
              <p className="text-lg text-white/80">
                Handles are portable, on-chain identities that unlock publishing, commerce, and messaging across mobile
                and web. Onboard in minutes and bring your audience with you.
              </p>
            </div>

            <div className="grid gap-4 rounded-[28px] border border-white/10 bg-white/5 p-6 shadow-[0_35px_120px_rgba(0,0,0,0.45)] backdrop-blur lg:grid-cols-2">
              {featureCards.map((item) => (
                <div key={item.label} className="rounded-2xl border border-white/10 bg-gradient-to-br from-white/10 to-transparent p-4">
                  <p className="text-xs font-semibold uppercase tracking-[0.25em] text-white/60">{item.label}</p>
                  <p className="mt-2 text-sm text-white/80">{item.copy}</p>
                </div>
              ))}
            </div>

            <div className="grid gap-4 lg:grid-cols-3">
              {sellingPoints.map((point) => (
                <div key={point.title} className="rounded-2xl border border-white/10 bg-black/30 p-4">
                  <p className="text-sm font-semibold text-white">{point.title}</p>
                  <p className="mt-2 text-xs text-white/70">{point.detail}</p>
                </div>
              ))}
            </div>
          </section>

          <form
            onSubmit={onSubmit}
            className="w-full space-y-5 rounded-[30px] border border-white/10 bg-[#050507]/90 p-8 shadow-[0_45px_140px_rgba(0,0,0,0.6)] backdrop-blur"
          >
            <div>
              <label className="text-sm font-semibold text-white/80">Full name</label>
              <input
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="Jordan Creator"
                className="mt-2 w-full rounded-2xl border border-white/15 bg-[#11101a] px-4 py-3 text-base text-white outline-none focus:border-white focus:ring-2 focus:ring-pink-400/60"
                autoComplete="name"
              />
            </div>

            <div>
              <label className="text-sm font-semibold text-white/80">Email</label>
              <input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                type="email"
                className="mt-2 w-full rounded-2xl border border-white/15 bg-[#11101a] px-4 py-3 text-base text-white outline-none focus:border-white focus:ring-2 focus:ring-pink-400/60"
                autoComplete="email"
              />
            </div>

            <div>
              <label className="text-sm font-semibold text-white/80">Phone (optional)</label>
              <input
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+15551234567"
                className="mt-2 w-full rounded-2xl border border-white/15 bg-[#11101a] px-4 py-3 text-base text-white outline-none focus:border-white focus:ring-2 focus:ring-pink-400/60"
              />
              <p className="mt-1 text-xs text-white/60">Add a phone to enable SMS later. Use +countrycode.</p>
            </div>

            <div>
              <label className="text-sm font-semibold text-white/80">Password</label>
              <div className="mt-2 flex items-center rounded-2xl border border-white/15 bg-[#11101a] px-4">
                <input
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="At least 8 characters"
                  type={showPw ? 'text' : 'password'}
                  className="flex-1 bg-transparent py-3 text-base text-white outline-none"
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPw((s) => !s)}
                  className="px-2 text-sm font-semibold text-white/70 hover:text-white"
                >
                  {showPw ? 'Hide' : 'Show'}
                </button>
              </div>
              <div className="mt-2 flex items-center gap-2">
                {[0, 1, 2, 3].map((i) => (
                  <div
                    key={i}
                    className={`h-2 flex-1 rounded-full transition ${i < pwStrength ? (i >= 2 ? 'bg-emerald-400' : 'bg-amber-400') : 'bg-white/10'}`}
                  />
                ))}
                <span className="text-xs font-semibold text-white/70">{strengthLabel}</span>
              </div>
            </div>

            <button
              type="submit"
              disabled={!ready || submitting}
            className="w-full rounded-2xl bg-[#1d4ed8] px-6 py-3 text-base font-bold text-white shadow-[0_20px_45px_rgba(0,0,0,0.45)] transition hover:bg-[#1a46c2] disabled:opacity-50"
            >
              {submitting ? 'Creating…' : 'Create account'}
            </button>

            {error && <p className="text-center text-sm text-rose-300">{error}</p>}
            {!error && info && <p className="text-center text-sm text-white/80">{info}</p>}

            <p className="text-center text-xs text-white/65">
              By creating an account, you agree to our{' '}
              <a href="/legal/terms" className="font-semibold text-white underline">
                Terms
              </a>{' '}
              and{' '}
              <a href="/legal/privacy" className="font-semibold text-white underline">
                Privacy Policy
              </a>
              .
            </p>

            <p className="text-center text-sm text-white/80">
              Already have an account?{' '}
              <Link to="/login" className="font-semibold text-white hover:underline">
                Sign in
              </Link>
            </p>
          </form>
        </div>
      </main>
    </div>
  )
}
